import { DestroyRef, Injectable, Signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { patchState, SignalState, signalState } from '@ngrx/signals';
import { UserProfile } from 'angular-api-gateway/v1/model/userProfile';
import { UsersService } from 'angular-api-gateway/v1/api/users.service';
import { UsersAvatarService } from 'angular-api-gateway/v1/api/usersAvatar.service';
import { AvatarResponse } from 'angular-api-gateway/v1/model/avatarResponse';
import { DeleteAvatarResponse } from 'angular-api-gateway/v1/model/deleteAvatarResponse';
import { UpdateUserProfileRequest } from 'angular-api-gateway/v1/model/updateUserProfileRequest';
import { defaultApiGatewayParams } from '@lentau/core/api/gateway';
import { REQUEST_STATE } from '@lentau/ui-kit/core/enums/request-state.enum';
import { ProfileUserStoreData, UserProfileUpdateOptions } from './user.types';
import { ToastService } from '@lentau/ui-kit/molecules/toast/service/toast.service';
import { clearPhone } from '@lentau/ui-kit/core/helpers/phone.helper';
import { CookieFacade } from '@lentau/ui-kit/core/services/cookie/cookie.facade';
import { SentryLogger } from '@lentau/core/sentry/shared/services/sentry-logger.service';
import { switchMap } from 'rxjs';
import { ToasterErrorMessage, ToasterSuccessMessage } from '@lentau/ui-kit/molecules/toast/shared/toast.types';
import { deleteOk, deleteFail } from './user.consts';
import { ToastErrorMessages, ToastSuccessMessages } from '@lentau/ui-kit/molecules/toast/shared/toast.const';
import { ErrorListEnum, SuccessListEnum } from '@lentau/ui-kit/molecules/toast/shared/toast.enums';
import { LuToastMessage } from '@lentau/ui-kit/molecules/toast/shared/lu-toast-message.interface';
import { COOKIES_USER_ID_KEY } from '@lentau/auth/shared/cookie.const';
import { toBase64 } from '@lentau/ui-kit/core/helpers/to-base64.helper';
import { UserProfileConsent } from 'angular-api-gateway/v1/model/userProfileConsent';
import { UserProfileSignalBridge } from '@lentau/profile/user/shared/services/user-profile-signal-bridge.service';

const initialState: ProfileUserStoreData = {
  user: {} as UserProfile,
  prevUser: {} as UserProfile,
  requestState: REQUEST_STATE.NONE,
  avatarUploadRequestState: REQUEST_STATE.NONE,
  isCacheInit: false,
};

@Injectable({ providedIn: 'root' })
export class UserStore {
  private destroyRef: DestroyRef = inject(DestroyRef);
  private usersService: UsersService = inject(UsersService);
  private usersAvatarService: UsersAvatarService = inject(UsersAvatarService);
  private toastService: ToastService = inject(ToastService);
  private cookieService: CookieFacade = inject(CookieFacade);
  private sentryLogger: SentryLogger = inject(SentryLogger);
  private userProfileSignalBridge: UserProfileSignalBridge = inject(UserProfileSignalBridge);

  private store: SignalState<ProfileUserStoreData> = signalState({ ...initialState });

  public user: Signal<UserProfile> = this.store.user;
  public prevUser: Signal<UserProfile> = this.store.prevUser;
  public requestState: Signal<REQUEST_STATE> = this.store.requestState;
  public isLoaded: Signal<boolean> = computed(() => this.requestState() === REQUEST_STATE.SUCCESS);
  public avatarUploadRequestState: Signal<REQUEST_STATE> = this.store.avatarUploadRequestState;
  public cellularPhone: Signal<string> = computed(() => this.store.user()?.cellularPhone);
  public segments: Signal<string[]> = computed(() => this.store.user()?.segments);
  public buyerId: Signal<number> = computed(() => this.store.user()?.buyerId);
  public crmClientNumber: Signal<string> = computed(() => this.store.user()?.crmClientNumber);
  public passportId: Signal<string> = computed(() => this.store.user()?.passportId);
  public legalEntityId: Signal<string> = computed(() => this.store.user()?.legalEntityId);
  public isNewcomer: Signal<boolean> = computed(() => this.store.user()?.isNewcomer);
  public avatar: Signal<string> = computed(() => this.store.user()?.avatar);
  public consent: Signal<UserProfileConsent> = computed(() => this.store.user()?.communicationConsents);
  public isCacheInit: Signal<boolean> = this.store.isCacheInit;
  public userProfileChanged: Signal<UserProfile | null> = computed(() => {
    const currentUser: UserProfile = this.store.user();
    const previousUser: UserProfile = this.store.prevUser();

    if (!currentUser?.passportId || !previousUser?.passportId) {
      return null;
    }

    const currentProfileId: string = `${currentUser.passportId}-${currentUser.legalEntityId || ''}`;
    const previousProfileId: string = `${previousUser.passportId}-${previousUser.legalEntityId || ''}`;

    if (currentProfileId !== previousProfileId) {
      return currentUser;
    }

    return null;
  });

  constructor() {
    this.userProfileSignalBridge.connect({
      buyerId: this.buyerId,
      crmClientNumber: this.crmClientNumber,
      passportId: this.passportId,
      legalEntityId: this.legalEntityId,
      isLoaded: this.isLoaded,
    });
  }

  public getUserProfile(): void {
    patchState(this.store, { requestState: REQUEST_STATE.LOADING });

    this.usersService
      .getUserProfile({ ...defaultApiGatewayParams })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user: UserProfile) => {
          this.cookieService.set(COOKIES_USER_ID_KEY, user?.buyerId?.toString());
          this.sentryLogger.setUserData({
            passportId: user?.passportId,
            legalEntityId: user?.legalEntityId,
          });
          patchState(this.store, {
            prevUser: { ...this.store.user() },
            user: { ...user, cellularPhone: clearPhone(user?.cellularPhone) },
            requestState: REQUEST_STATE.SUCCESS,
          });
        },
        error: () => {
          patchState(this.store, { ...initialState, requestState: REQUEST_STATE.FAIL });
        },
      });
  }

  public updateUserProfile(
    updateUserProfileRequest: UpdateUserProfileRequest,
    message?: LuToastMessage,
    options: UserProfileUpdateOptions = {}
  ): void {
    this.setProfileUpdateRequestState(REQUEST_STATE.LOADING, options);

    this.usersService
      .updateUserProfile({ ...defaultApiGatewayParams, updateUserProfileRequest })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user: UserProfile) => this.completeProfileUpdate(user, message, options),
        error: (error: unknown) => this.failProfileUpdate(error, options),
      });
  }

  private completeProfileUpdate(
    user: UserProfile,
    message: LuToastMessage | undefined,
    options: UserProfileUpdateOptions
  ): void {
    this.showProfileUpdateSuccess(message, options);
    patchState(this.store, {
      user: this.resolveUpdatedProfile(user, options.mergeProfile),
    });
    this.setProfileUpdateRequestState(REQUEST_STATE.SUCCESS, options);
    options.onSuccess?.();
  }

  private failProfileUpdate(error: unknown, options: UserProfileUpdateOptions): void {
    if (options.showToast !== false) {
      this.toastService.show([{ ...ToastErrorMessages.get(ErrorListEnum.ProfileModify) }]);
    }
    this.setProfileUpdateRequestState(REQUEST_STATE.FAIL, options);
    options.onError?.(error);
  }

  private showProfileUpdateSuccess(message: LuToastMessage | undefined, options: UserProfileUpdateOptions): void {
    if (options.showToast === false) return;

    this.toastService.show([message ?? { ...ToastSuccessMessages.get(SuccessListEnum.ProfileModify) }]);
  }

  private resolveUpdatedProfile(user: UserProfile, mergeProfile: boolean = false): UserProfile {
    const profile: UserProfile = mergeProfile ? { ...this.store.user(), ...user } : user;

    return { ...profile, cellularPhone: clearPhone(profile?.cellularPhone) };
  }

  private setProfileUpdateRequestState(requestState: REQUEST_STATE, options: UserProfileUpdateOptions): void {
    if (options.updateRequestState === false) return;

    patchState(this.store, { requestState });
  }

  public reset(): void {
    patchState(this.store, { ...initialState });
  }

  public setProfileCache(userProfile: Partial<UserProfile>): void {
    patchState(this.store, {
      user: { ...this.store.user(), ...userProfile },
      prevUser: { ...this.store.prevUser(), ...userProfile },
      isCacheInit: true,
    });
  }

  public patchUsersAvatar(file: File): void {
    patchState(this.store, { avatarUploadRequestState: REQUEST_STATE.LOADING });

    toBase64(file)
      .pipe(
        switchMap((avatar: string) =>
          this.usersAvatarService.patchUsersAvatar({ ...defaultApiGatewayParams, avatarRequest: { avatar } })
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (response: AvatarResponse) => {
          patchState(this.store, {
            user: { ...this.store.user(), avatar: response.avatarUrl },
            requestState: REQUEST_STATE.SUCCESS,
            avatarUploadRequestState: REQUEST_STATE.SUCCESS,
          });
        },
        error: () => {
          patchState(this.store, {
            requestState: REQUEST_STATE.FAIL,
            avatarUploadRequestState: REQUEST_STATE.FAIL,
          });
        },
      });
  }

  public deleteUsersAvatar(): void {
    this.usersAvatarService
      .deleteUsersAvatar({ ...defaultApiGatewayParams })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: DeleteAvatarResponse) => {
          patchState(this.store, {
            user: { ...this.store.user(), avatar: null },
          });

          if (response.success) {
            this.toastService.show([new ToasterSuccessMessage(deleteOk)]);
          } else {
            this.toastService.show([new ToasterErrorMessage(deleteFail)]);
          }
        },
      });
  }

  public updateLegalEntityId(legalEntityId: string): void {
    patchState(this.store, { user: { ...this.store.user(), legalEntityId: legalEntityId } });
  }
}
